/**
 * Table DISTRIBUTION, shared by the RDV and Data Mart modelling blades.
 *
 * DISTRIBUTION only applies to Synapse dedicated SQL pool targets. The backend writes
 * the stored token straight into `WITH (DISTRIBUTION = ...)`, so the UI must send the
 * canonical T-SQL token — label text like "hash key" produced SQL that cannot parse.
 */

export const DISTRIBUTION_ROUND_ROBIN = "ROUND_ROBIN";
export const DISTRIBUTION_HASH = "HASH";
export const DISTRIBUTION_REPLICATE = "REPLICATE";
/** Legacy marker on non-generated entities (RDV Manual, DM facts). Not selectable. */
export const DISTRIBUTION_NOT_APPLICABLE = "N/A";

export const DISTRIBUTION_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "Round robin", value: DISTRIBUTION_ROUND_ROBIN },
  { label: "Hash", value: DISTRIBUTION_HASH },
  { label: "Replicate", value: DISTRIBUTION_REPLICATE }
];

const ALIASES: Record<string, string> = {
  ROUND_ROBIN: DISTRIBUTION_ROUND_ROBIN,
  ROUNDROBIN: DISTRIBUTION_ROUND_ROBIN,
  "ROUND ROBIN": DISTRIBUTION_ROUND_ROBIN,
  HASH: DISTRIBUTION_HASH,
  HASHKEY: DISTRIBUTION_HASH,
  "HASH KEY": DISTRIBUTION_HASH,
  HASH_KEY: DISTRIBUTION_HASH,
  REPLICATE: DISTRIBUTION_REPLICATE,
  REPLICATED: DISTRIBUTION_REPLICATE,
  "N/A": DISTRIBUTION_NOT_APPLICABLE,
  NA: DISTRIBUTION_NOT_APPLICABLE,
  N_A: DISTRIBUTION_NOT_APPLICABLE
};

/** Canonical token for a stored/entered value, or "" when unset or unrecognized. */
export const normalizeDistributionType = (value: string | null | undefined): string => {
  const text = (value ?? "").trim();
  if (!text) {
    return "";
  }
  return ALIASES[text.toUpperCase().replace(/-/g, "_")] ?? "";
};

export const isHashDistribution = (value: string | null | undefined): boolean =>
  normalizeDistributionType(value) === DISTRIBUTION_HASH;

/** Distribution is a Synapse dedicated SQL pool concept and shown for nothing else. */
export const isSqlPoolDedicated = (value: string | null | undefined): boolean =>
  (value ?? "").toString().trim().toUpperCase() === "SQLPOOL_DEDICATED";

/** Blocking validation message for a distribution pair, or null when it is valid. */
export const getDistributionValidationError = (
  distributionType: string | null | undefined,
  distributionKey: string | null | undefined
): string | null => {
  if (!isHashDistribution(distributionType)) {
    return null;
  }
  return (distributionKey ?? "").trim()
    ? null
    : "Distribution key is required when distribution type is Hash.";
};
